import { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth,
  db 
} from '../firebase';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider,
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { 
    doc, 
    setDoc, 
    getDoc, 
    query, 
    collection, 
    where, 
    getDocs 
  } from 'firebase/firestore';
const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('openai_api_key') || '');

  const updateUserProfile = async (data) => {
    if (!user) return;
    
    try {
      // Check if username is unique
      if (data.username) {
        const usernameQuery = query(
          collection(db, 'users'), 
          where('username', '==', data.username)
        );
        const usernameSnapshot = await getDocs(usernameQuery);
        
        if (!usernameSnapshot.empty && usernameSnapshot.docs[0].id !== user.uid) {
          throw new Error('Username already taken');
        }
      }
      
      // Update auth profile
      if (data.displayName) {
        await updateProfile(user, { displayName: data.displayName });
      }
      
      // Update additional data in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        name: data.displayName || '',
        username: data.username || '',
        bio: data.bio || '',
        website: data.website || '',
        company: data.company || '',
        location: data.location || '',
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const logout = () => {
    return signOut(auth);
  };

  const updateApiKey = (newKey) => {
    setApiKey(newKey);
    localStorage.setItem('openai_api_key', newKey);
  };

  const value = {
    user,
    apiKey,
    login,
    loginWithGoogle,
    logout,
    updateApiKey,
    updateUserProfile,
    updateEmail,
    updatePassword,
    EmailAuthProvider,
    reauthenticateWithCredential
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}